import Box from '@mui/material/Box';
import type { SxProps, Theme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { useMemo } from 'react';

import { type Address as AddressType } from '@/core/graphql/generated/types';
import { getAddressParts } from '@/utils/getAddressParts';
import { isIntlDiff } from '@/utils/isValidIntl';

import { addressStyles as sx } from './Address.styles';
import { AddressLine } from './AddressLine';

export type AddressProps = {
	address: AddressType | undefined | null;
	inline?: boolean;
	text?: boolean;
	withInternational?: boolean;
	view?: 'basic' | 'summary' | 'full' | 'all';
	withoutCountry?: boolean;
	customSx?: SxProps<Theme>;
};

const hasIntlToShow = (a: AddressType): boolean => {
	const pairs: Array<[string | null | undefined, string | null | undefined]> = [
		[a.streetAddress1Cda_V, a.streetAddress1Intl_V],
		[a.streetAddress2Cda_V, a.streetAddress2Intl_V],
		[a.cityCda_V, a.cityIntl_V],
		[a.postalCodeCda_V, a.postalCodeIntl_V]
	];
	return pairs.some(([p, i]) => !!p && !!i && isIntlDiff(p, i));
};

export const Address = ({
	address,
	withInternational = true,
	inline = false,
	text = false,
	view = 'full',
	withoutCountry = false,
	customSx
}: AddressProps) => {
	const lines = useMemo(
		() =>
			address
				? getAddressParts(address, {
						withInternational,
						view,
						includeCountry: !withoutCountry
					})
				: [],
		[address, withInternational, view, withoutCountry]
	);

	const needsPerFieldIntl = useMemo(
		() => !!address && withInternational && hasIntlToShow(address),
		[address, withInternational]
	);

	if (!address) return;
	if (inline) {
		if (text) {
			return lines.join(', ');
		}
		return (
			<Typography
				component='span'
				sx={[sx.line, ...(Array.isArray(customSx) ? customSx : [customSx])]}
			>
				{lines.join(', ')}
			</Typography>
		);
	}

	const includeStreet = view !== 'basic';
	const includeStreet2 = view === 'full' || view === 'all';
	const includePostcode = view !== 'basic';
	const isAll = view === 'all';

	if (!needsPerFieldIntl) {
		return (
			<Box sx={customSx}>
				{lines.map((line, i) => (
					<Typography key={`${line}-${i}`} sx={sx.line}>
						{line}
					</Typography>
				))}
			</Box>
		);
	}

	return (
		<Box sx={customSx}>
			{includeStreet && (
				<>
					<AddressLine
						primary={address.streetAddress1Cda_V}
						intl={address.streetAddress1Intl_V}
						showIntl={withInternational}
					/>
					{includeStreet2 && (
						<AddressLine
							primary={address.streetAddress2Cda_V}
							intl={address.streetAddress2Intl_V}
							showIntl={withInternational}
						/>
					)}
				</>
			)}

			<AddressLine
				primary={address.cityCda_V}
				intl={address.cityIntl_V}
				showIntl={withInternational}
			/>
			<Typography sx={sx.line}>{address.stateCda_V}</Typography>

			{includePostcode && (
				<AddressLine
					primary={address.postalCodeCda_V}
					intl={address.postalCodeIntl_V}
					showIntl={withInternational}
				/>
			)}

			{isAll && address.postalCodeFullUs_V && (
				<Typography sx={sx.line}>{address.postalCodeFullUs_V}</Typography>
			)}

			{isAll && (
				<>
					{address.subStateAdminUnit_V && (
						<Typography sx={sx.line}>{address.subStateAdminUnit_V}</Typography>
					)}
					{address.cbsa_V && (
						<Typography sx={sx.line}>{address.cbsa_V}</Typography>
					)}
				</>
			)}
		</Box>
	);
};
